import {
  Box,
  Button,
  CloseButton,
  Dialog,
  HStack,
  Portal,
  Select,
  Switch,
  Text,
  VStack,
  createListCollection,
} from '@chakra-ui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearch } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CiExport } from 'react-icons/ci';
import * as XLSX from 'xlsx';

import { EntitiesService, IndicatorYearPeriodPublic, IndicatorsPeriodsService, IndicatorsService } from '@/client';
import { Tooltip } from '@/components/ui/tooltip';
import useMediaQuery from '@/hooks/use-media-query';
import { snakeCaseString } from '@/utils';

type IndicatorYearPeriodPublicWithOptionnalEntity = IndicatorYearPeriodPublic & {
  entity_id?: string;
  entity_name?: string;
};

function getEntitiesQueryOptions({ slug }: { slug: string }) {
  return {
    queryKey: ['entities', { slug }],
    queryFn: async () => {
      return await EntitiesService.readEntities({ companySlug: slug });
    },
  };
}

function getIndicatorsProgressionQueryOptions({ slug }: { slug: string }) {
  return {
    queryKey: ['indicators_progression'],
    queryFn: async () => {
      return await IndicatorsService.readIndicatorsProgression({
        companySlug: slug,
      });
    },
  };
}

function getIndicatorsPeriodsQueryOptions({
  slug,
  year,
  entity_id = undefined,
  entity_name = undefined,
  filter,
  sort,
  search,
  onlySimple = false,
}: {
  slug: string;
  year: number;
  entity_id?: string;
  entity_name?: string;
  filter: string;
  sort: string;
  search: string;
  onlySimple?: boolean;
}) {
  return {
    queryKey: ['indicators_periods', { entity_id, entity_name, filter, sort, search, onlySimple }],
    queryFn: async () => {
      const filterPath = entity_id
        ? 'indicator_year_entity___indicator_year_setting___indicator___category'
        : 'indicator_year_setting___indicator___category';

      const filtersWithCategory = onlySimple ? `${filterPath}=simple` : undefined;

      return await IndicatorsPeriodsService.readIndicatorPeriods({
        year,
        entityId: entity_id,
        companySlug: slug,
        filters: filter ? `${filter}&${filtersWithCategory}` : filtersWithCategory,
        offset: 0,
        limit: 9999,
        sort,
        search,
      });
    },
    retry: true,
  };
}

export const CollecteExportDialog = ({ default_entity_id = 'consolidate' }: { default_entity_id?: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { slug, year } = useParams({ strict: false });
  const { filter, search, sort } = useSearch({ strict: false });
  const [entityIdSelected, setEntityIdSelected] = useState<string[]>([default_entity_id]);
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState(0);
  const [globalExport, setGlobalExport] = useState(false);
  const [onlySimple, setOnlySimple] = useState(true);

  const { data: indicatorsProgression, isSuccess: indicatorsSuccess } = useQuery({
    ...getIndicatorsProgressionQueryOptions({ slug }),
  });

  useMemo(() => {
    setEntityIdSelected([!!default_entity_id ? default_entity_id : 'consolidate']);
  }, [default_entity_id]);

  const {
    data: entities,
    isLoading: isLoadingEntities,
    isSuccess: entitiesSuccess,
  } = useQuery({
    ...getEntitiesQueryOptions({ slug }),
  });

  const entitiesByYear = useMemo(() => {
    if (!indicatorsSuccess || !entitiesSuccess || !year) return [];

    const filteredIndicators = indicatorsProgression.data.filter((item) => item.year === parseInt(year, 10));

    const allEntities = filteredIndicators.flatMap((item) => item.entities ?? []);

    const uniqueEntities = [...new Map(allEntities.map((e) => [e?.id, e])).values()];

    return uniqueEntities.map((e) => {
      const matched = entities.data.find((ent) => ent.id === e?.id);
      const entity = matched ?? e;
      return {
        name: entity.name,
        value: entity.id,
      };
    });
  }, [indicatorsProgression, year, entities, indicatorsSuccess, entitiesSuccess]);

  const createEntitiesCollection = (entities: { name: string; value: string }[]) => {
    if (!entities) return;

    return createListCollection({
      items: [
        { label: 'Consolidé', value: 'consolidate' },
        ...entities.map((entity) => ({
          label: entity.name,
          value: entity.value,
        })),
      ],
    });
  };

  const entitiesFramework = useMemo(() => {
    if (!entitiesByYear || entitiesByYear.length === 0) {
      setEntityIdSelected(['consolidate']);
      return undefined;
    }
    return createEntitiesCollection(entitiesByYear);
  }, [entitiesByYear]);

  const transformFields = (
    row: IndicatorYearPeriodPublicWithOptionnalEntity,
    addEntity?: boolean
  ): Record<string, any> => {
    const formatNumberField = (val?: string | number | null) => {
      if (val === null || val === undefined) return '';
      return String(val).replace(/,/g, '.').replace(/\s+/g, '');
    };

    let data: Record<string, any> = {
      id: row.id ?? '',
      indicator_name: row.indicator_name?.trim() ?? '',
      indicator_id: row.indicator_id ?? '',
      commitment_name: row.commitment_name?.trim() ?? '',
      pillar_name: row.pillar_name?.trim() ?? '',
      value: formatNumberField(row.value),
      objective: formatNumberField(row.objective),
      indicator_unit: row.indicator_unit?.trim() ?? '',
    };

    if (addEntity) {
      data.entity_id = row.entity_id ?? '';
      data.entity_name = row.entity_name?.trim() ?? '';
    }
    return data;
  };

  const handleExport = async () => {
    try {
      setIsLoading(true);
      setProgress(0);

      const hasNoSelection = entityIdSelected[0] === 'consolidate';
      const baseQueryConfig = {
        slug,
        year,
        filter,
        sort,
        search,
        onlySimple,
      };

      const selectedEntities = !globalExport
        ? (entities?.data.filter((entity) => entityIdSelected.includes(entity.id)) ?? [])
        : (entities?.data ?? []);
      const entitiesQueriesConfig = selectedEntities.map((entity) => ({
        entity_id: entity.id,
        entity_name: entity.name,
      }));

      const allQueriesConfig = globalExport
        ? [baseQueryConfig, ...entitiesQueriesConfig.map((config) => ({ ...baseQueryConfig, ...config }))]
        : hasNoSelection
          ? [baseQueryConfig]
          : entitiesQueriesConfig.map((config) => ({ ...baseQueryConfig, ...config }));

      const allQueries = allQueriesConfig.map((config) => getIndicatorsPeriodsQueryOptions(config));

      const total = allQueries.length;
      let completed = 0;
      const allData: IndicatorYearPeriodPublicWithOptionnalEntity[] = [];

      await Promise.all(
        allQueries.map(async (query) => {
          try {
            const result = await queryClient.fetchQuery(query);
            const itemsRaw = Array.isArray(result) ? result : (result?.data ?? []);
            const items = itemsRaw.map((item) => {
              if (
                typeof query.queryKey[1] === 'object' &&
                query.queryKey[1] !== null &&
                'entity_id' in query.queryKey[1]
              ) {
                return {
                  ...item,
                  entity_id: (query.queryKey[1] as { entity_id?: string })?.entity_id ?? '',
                  entity_name: (query.queryKey[1] as { entity_name?: string })?.entity_name ?? '',
                };
              }
              return item;
            });
            allData.push(...items);
          } catch (error) {
            console.error('Query failed:', error);
          } finally {
            completed += 1;
            setProgress(completed / total);
          }
        })
      );

      if (!allData || allData.length === 0) {
        alert('Aucune donnée à exporter.');
        setIsLoading(false);
        return;
      }

      const cleanedData = onlySimple ? allData.filter((item) => item.id !== null) : allData;

      const flatData = cleanedData.filter((item) => item).map((item) => transformFields(item, globalExport));

      const worksheet = XLSX.utils.json_to_sheet(flatData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Indicators');

      const now = new Date();
      const timestamp = now.toISOString().slice(0, 16).replace('T', '_').replace(':', '-');
      const filename = `indicators_${year}_collecte_${timestamp}_${globalExport ? 'global' : hasNoSelection ? 'consolidated' : snakeCaseString(selectedEntities[0].name)}.xlsx`;

      XLSX.writeFile(workbook, filename);
    } catch (error) {
      console.error('Erreur lors de la génération de l’export :', error);
    } finally {
      setIsLoading(false);
    }
  };

  const isTooltipHidden = useMediaQuery('(min-width: 1250px)');

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(e) => {
        setIsOpen(e.open);
      }}
      placement="center"
      size="lg"
    >
      <Dialog.Trigger as="div">
        <Tooltip
          showArrow
          content={t('ui.export')}
          openDelay={100}
          closeDelay={100}
          contentProps={{ css: { '--tooltip-bg': 'colors.primary' } }}
          disabled={isTooltipHidden}
        >
          <Button variant="outline" size="sm" {...(!isTooltipHidden && { p: '0' })}>
            <CiExport />
            {isTooltipHidden && <span>{t('ui.export')}</span>}
          </Button>
        </Tooltip>
      </Dialog.Trigger>

      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header flex="flex" flexDirection="column">
              <Dialog.Title>Export de données pour l'année {year}</Dialog.Title>
              <Text mb="2">Exportez vos données afin de les analyser ou les archiver.</Text>
            </Dialog.Header>

            <Dialog.Body>
              <VStack rowGap={3} alignItems="start">
                {!isLoadingEntities && entitiesFramework && (
                  <HStack w="full" justifyContent="space-between" alignItems={'start'}>
                    <VStack alignItems="start">
                      <Text fontWeight="bold" fontSize="sm">
                        Export global des données
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        Exportez toutes les données collectées pour l'année sélectionnée, sans distinction de périmètre.
                      </Text>
                    </VStack>
                    <Switch.Root checked={globalExport} onCheckedChange={(e) => setGlobalExport(e.checked)}>
                      <Switch.HiddenInput />
                      <Switch.Control />
                    </Switch.Root>
                  </HStack>
                )}
                <HStack w="full" justifyContent="space-between" alignItems={'start'}>
                  {!isLoadingEntities && entitiesFramework && !globalExport && (
                    <>
                      <VStack alignItems="start">
                        <Text fontWeight="bold" fontSize="sm">
                          Séléctionner le périmètre
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          Séléctionner le périmètre où se fera l'export
                        </Text>
                      </VStack>
                      <HStack justifyContent={'flex-start'}>
                        <Select.Root
                          collection={entitiesFramework}
                          width="300px"
                          value={entityIdSelected}
                          onValueChange={(e) => setEntityIdSelected(e.value)}
                        >
                          <Select.HiddenSelect />
                          <Select.Control>
                            <Select.Trigger>
                              <Select.ValueText placeholder="Selectionner le perimetre" />
                            </Select.Trigger>
                            <Select.IndicatorGroup>
                              <Select.Indicator />
                            </Select.IndicatorGroup>
                          </Select.Control>
                          <Select.Positioner>
                            <Select.Content>
                              {entitiesFramework.items.map((framework) => (
                                <Select.Item item={framework} key={framework.value}>
                                  {framework.label}
                                  <Select.ItemIndicator />
                                </Select.Item>
                              ))}
                            </Select.Content>
                          </Select.Positioner>
                        </Select.Root>
                      </HStack>
                    </>
                  )}
                </HStack>
                <HStack w="full" justifyContent="space-between" alignItems={'start'}>
                  <VStack alignItems="start">
                    <Text fontWeight="bold" fontSize="sm">
                      Exporter uniquement les données simples
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      N'exporte que les données liées à des indicateurs simples, excluant celles liées à des indicateurs
                      composés ou calculés.
                    </Text>
                  </VStack>
                  <Switch.Root checked={onlySimple} onCheckedChange={(e) => setOnlySimple(e.checked)}>
                    <Switch.HiddenInput />
                    <Switch.Control />
                  </Switch.Root>
                </HStack>
              </VStack>

              {isLoading && (
                <VStack
                  w="full"
                  alignItems="center"
                  justifyContent="center"
                  py={6}
                  bg="whiteAlpha.800"
                  borderRadius="md"
                >
                  <Text fontWeight="medium" color="gray.600">
                    Export en cours... ({Math.round(progress * 100)}%)
                  </Text>

                  <Box w="80%" bg="gray.200" borderRadius="full" overflow="hidden" h="8px">
                    <Box
                      w={`${Math.min(100, Math.round(progress * 100))}%`}
                      h="8px"
                      bg="#1FA285"
                      transition="width 0.2s ease"
                    />
                  </Box>

                  <Text fontSize="xs" color="gray.500">
                    Ne fermez pas cette fenêtre pendant l’export.
                  </Text>
                </VStack>
              )}
            </Dialog.Body>

            <Dialog.Footer>
              <Dialog.ActionTrigger asChild>
                <Button variant="outline">{t('ui.cancel')}</Button>
              </Dialog.ActionTrigger>
              <Button loading={isLoading} onClick={handleExport} loadingText="Export" spinnerPlacement="end">
                {t('ui.export')}
              </Button>
            </Dialog.Footer>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
